import { format, parse } from 'date-fns-modern';
import { tz } from '@date-fns/tz';

export const formatDate = (date: string) => {
  const dateObject = parse(date, 'yyyy-MM-dd', new Date());
  dateObject.setHours(0, 0, 0, 0);
  return format(dateObject, 'yyyy-MM-dd');
};

export const formatFromMonthFirst = (date: string): string => {
  return format(parse(date, 'MM/dd/yyyy', new Date()), 'yyyy-MM-dd');
};

export const formatFromYearFirst = (date: string): string => {
  return format(parse(date, 'yyyy-MM-dd', new Date()), 'MM/dd/yyyy');
};

export const getTZFromDateString = (date: string): string => {
  let timeZone = 'America/New_York';
  const timeZoneMatch = date.match(/([\+-]\d{2}:\d{2})$/);
  if (timeZoneMatch && timeZoneMatch.length > 0) {
    timeZone = timeZoneMatch[1];
  }
  return timeZone;
};

export const formatTime24Hour = (date: string): string => {
  return format(date, 'HH:mm', { in: tz(getTZFromDateString(date)) });
};

export const formatTimeLC = (date: string): string => {
  return format(date, 'hh:mm aaa', { in: tz(getTZFromDateString(date)) });
};

export const formatTimeUC = (date: string): string => {
  return format(date, 'hh:mm aa', { in: tz(getTZFromDateString(date)) });
};

export const formatShortMonthDayYear = (date: string): string => {
  return format(date, 'MM/dd/yyyy', { in: tz(getTZFromDateString(date)) });
};

export const formatDateToYearMonthDay = (date: string): string => {
  const [month, day, year] = date.split('/').map(Number);
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

export const formatDateToYearMonth = (date: string): string => {
  const [month, year] = date.split('/').map(Number);
  return `${year}-${String(month).padStart(2, '0')}`;
};

export const formatDurationISO8601 = (durationMinutes: number): string => {
  const hours = Math.floor(durationMinutes / 60);
  const minutes = durationMinutes % 60;
  return `PT${hours > 0 ? `${hours}H` : ''}${minutes > 0 ? `${minutes}M` : ''}`;
};

export const formatDurationISO8601Full = (totalSeconds: number): string => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return `PT${hours > 0 ? `${hours}H` : ''}${minutes > 0 ? `${minutes}M` : ''}${seconds > 0 ? `${seconds}S` : ''}`;
};


export const formatReadableMonthDay = (dateString: string, locale: string): string => {
  try {
    if (dateString && dateString.length == 10) {
      // YYYY-MM-DD or MM/DD/YYYY
      dateString += ' 12:00:00'; // Add time to avoid timezone issues
    }
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = { month: 'long', day: 'numeric' };
    return new Intl.DateTimeFormat(locale, options).format(date);
  } catch (error) {}
  return dateString;
};

export const formatReadableShortMonthDay = (dateString: string, locale: string): string => {
  try {
    if (dateString && dateString.length == 10) {
      // YYYY-MM-DD or MM/DD/YYYY
      dateString += ' 12:00:00'; // Add time to avoid timezone issues
    }
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = { month: 'short', day: '2-digit' };
    const formattedDate = new Intl.DateTimeFormat(locale, options).format(date);
    const [month, day] = formattedDate.split(' ');
    const shortMonth = month === 'May' ? month : `${month}.`;
    return `${shortMonth} ${day}`;
  } catch (error) {}
  return dateString;
};

export const formatReadableDate = (dateString: string, locale: string): string => {
  try {
    if (dateString && dateString.length == 10) {
      // YYYY-MM-DD or MM/DD/YYYY
      dateString += ' 12:00:00'; // Add time to avoid timezone issues
    }
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Intl.DateTimeFormat(locale, options).format(date);
  } catch (error) {}
  return dateString;
};